import { useCanvasFlowSessions } from "@/api/canvas";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
  useFlowSessionById,
} from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import ChatArea from "@/components/ChatArea";
import { MessageInput } from "@/components/MessageInput";
import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MessageInputControlSet } from "@/constants/message-input";
import { MessageInputProvider } from "@/context/MessageInputContext";
import { SessionProvider } from "@/context/SessionContext";
import { DropZoneProvider, useOnDrop } from "@/dnd/OsFileDropLayer";
import { useMessageActions } from "@/hooks/useMessageActions";
import Sentry from "@/integrations/Sentry";
import { cn } from "@/utils";
import {
  FLOW_SESSION_MODES,
  PostMessageParams,
  ReferencedEntity,
} from "@cohesive/api-client";
import { SuggestionPills } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  FileText,
  HelpCircle,
  Lightbulb,
  Loader2,
  Mail,
  Maximize2,
  MessagesSquare,
  Minimize2,
  Minus,
  Plus,
  Sparkles,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SESSION_MESSAGE_INPUT_CLASSNAMES } from "../config/canvasSessionMessageInputClassNames";
import { type CanvasFloatingSessionHistory } from "../context/CanvasFloatingChatContext";
import { useCanvasOnboardingState } from "../context/CanvasOnboardingContext";
import { useCanvasReasoningEffort } from "../hooks/useCanvasReasoningEffort";

/** Compact relative time for the inline header timestamp — "now", "1m ago", "3h ago", … */
function shortRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return "";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 45) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

export interface CanvasSessionPaneContentProps {
  canvasId: string;
  flowSessionId: string;
  canGoBack: boolean;
  onGoBack: () => void;
  onClose: () => void;
  /** True when in the full-height (docked) size class — drives the expand-toggle icon. */
  isChatExpanded: boolean;
  /** Flip between windowed and full-height size classes. */
  onToggleExpand: () => void;
  onOpenSession: (
    sessionId: string,
    history?: CanvasFloatingSessionHistory,
  ) => void;
  /** Notifies the shell when the session-switcher dropdown opens. */
  onMenuOpenChange?: (open: boolean) => void;
  /** Canvas selection entities (owned by the panel; shared with the floating pills). */
  referencedEntities: ReferencedEntity[];
  onRemoveEntity: (entityId: string) => void;
  clearTextSelectionsFromMessage: (
    entities: ReferencedEntity[] | undefined,
  ) => void;
}

// Suggested actions for the canvas agent empty state
interface SuggestedCanvasAction {
  id: string;
  label: string;
  message: string;
  icon: LucideIcon;
}

const SUGGESTED_ACTIONS: SuggestedCanvasAction[] = [
  {
    id: "add-emails",
    label: "Add my recent emails",
    message: "Add my last 10 emails to the canvas",
    icon: Mail,
  },
  {
    id: "brainstorm",
    label: "Brainstorm ideas",
    message:
      "Give me 5 creative product ideas as text notes. Use markdown formatting and different colors creatively to make them visually engaging!",
    icon: Lightbulb,
  },
  {
    id: "create-outline",
    label: "Create a project outline",
    message: "Create a project outline document for a new initiative",
    icon: FileText,
  },
  {
    id: "help",
    label: "What can you do?",
    message: "What are all the things you can help me with on this canvas?",
    icon: HelpCircle,
  },
];

// Inner component that has access to SessionProvider
function CanvasSessionPaneInner({
  flowSessionId,
  canvasId,
  referencedEntities,
  onRemoveEntity,
  clearTextSelectionsFromMessage,
}: {
  flowSessionId: string;
  canvasId: string;
  /** Selected canvas entities (owned by the panel so they can also float above the input). */
  referencedEntities: ReferencedEntity[];
  onRemoveEntity: (entityId: string) => void;
  clearTextSelectionsFromMessage: (
    entities: ReferencedEntity[] | undefined,
  ) => void;
}) {
  // Check if session has any messages
  const {
    data: messages = [],
    refetch: refetchMessages,
    isPending: isMessagesPending,
  } = useFlowMessages(flowSessionId, undefined, {
    enabled: !!flowSessionId,
  });

  // Track current session status so we can disable the input while processing
  const { data: flowSession } = useFlowSessionById(flowSessionId, {
    enabled: !!flowSessionId,
  });
  const isSendDisabled = flowSession
    ? flowSession.status !== "ready" && flowSession.status !== "initialized"
    : false;

  // Onboarding chats run in a dedicated mode. Their composer is locked to the
  // scripted prompt — the user can send it but not edit or type their own
  // message to the onboarding agent.
  const isOnboardingSession =
    flowSession?.mode === FLOW_SESSION_MODES.CANVAS_ONBOARDING;

  const [reasoningEffort, setReasoningEffort] =
    useCanvasReasoningEffort(canvasId);

  // Canvas-scoped chat uploads land as `class=canvas` file_records (per the
  // Files attached in canvas-scoped chat are flow-session-scoped
  // (matches standalone chat). They ride on the message via
  // `file_record_ids` and the agent reads them through `file-list` /
  // `file-get`, but they don't auto-spawn a Loro node on the canvas.
  // Users who want a file ON the canvas drop it directly via the
  // canvas drop target / paste / quick-add surfaces.
  const {
    onSendMessage: onSendMessageBase,
    isLoading,
    uploadedFiles,
    onAttachFile,
    onRemoveFile,
  } = useMessageActions({
    onMessageSent: () => {
      setTimeout(() => refetchMessages(), 500);
    },
  });

  // Own the chat drop target so dropped files land on this hook's upload
  // queue (the visible composer), not the hidden ChatArea instance.
  useOnDrop(onAttachFile);

  // Onboarding can pin a pre-filled composer prompt. When set, the
  // MessageInput's Lexical editor is hydrated with the prompt text as
  // its default value — the user can edit it before sending. On send,
  // we dispatch a window event so the active step can react (e.g.
  // start the frontend artifact-seed pipeline) without needing a
  // direct callback ref into the step.
  const { pinnedComposerPrompt, activeOnboarding } = useCanvasOnboardingState();
  // Onboarding suppresses the empty-state welcome card + suggested
  // actions in the canvas chat. They distract from the onboarding
  // overlay's own focused prompt and pull the user toward off-flow
  // tangents. Auto-derived from the presence of an active onboarding
  // session — no explicit prop wiring needed at call sites.
  const suppressComposerSuggestions = !!activeOnboarding;

  const onSendMessage = useCallback(
    async (params: PostMessageParams) => {
      await onSendMessageBase(params);
      clearTextSelectionsFromMessage(params.referenced_entities);
      // Only a send from the dedicated onboarding session may advance a
      // scripted step. Without this guard, a user who switched the
      // floating chat to the regular canvas agent could fire the
      // step-advance event from the wrong conversation.
      if (pinnedComposerPrompt && isOnboardingSession) {
        window.dispatchEvent(
          new CustomEvent("cohesive:onboarding:composer-prompt-sent", {
            detail: { id: pinnedComposerPrompt.id },
          }),
        );
      }
    },
    [
      onSendMessageBase,
      pinnedComposerPrompt,
      isOnboardingSession,
      clearTextSelectionsFromMessage,
    ],
  );

  const handleSuggestedAction = useCallback(
    (action: SuggestedCanvasAction) => {
      onSendMessage({
        id: uuidv4(),
        content: action.message,
        referenced_entities: referencedEntities,
      } as PostMessageParams);
    },
    [onSendMessage, referencedEntities],
  );

  const hasMessages = messages.length > 0;

  // IMPORTANT: MessageInput is rendered unconditionally at the bottom so that
  // AudioRecordingButton / useAudioRecording never unmount while recording is
  // active. The previous design conditionally swapped MessageInput ↔ ChatArea
  // based on hasMessages, which caused the stream tracks to be stopped (via
  // useAudioRecording's cleanup effect) the moment React Query resolved its
  // first fetch for a session that already had messages — making recording
  // appear to immediately stop and kick off transcription.
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Upper area: nothing while fetching, then history OR empty-state welcome */}
      {isMessagesPending ? (
        <div className="flex-1 min-h-0" aria-busy />
      ) : hasMessages ? (
        // Full-bleed: the scroll area runs to the panel edges (scrollbar snug,
        // not inset). Horizontal inset lives on the message content + composer
        // instead, so messages, COT rail, and input share one 16px grid.
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full flex flex-col w-full scrollbar-hide">
            {/* hideMessageInput=true — the stable input below handles sending */}
            <ChatArea
              location="sidebar"
              placeholder="What would you like me to do?"
              className="!h-full"
              controlSet={MessageInputControlSet.CANVAS_CHAT}
              hideMessageInput={true}
              referencedEntities={referencedEntities}
              onSendSuccess={clearTextSelectionsFromMessage}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-y-auto">
          {/* Design-system chat empty state: Sparkles prompt + suggestion pills. */}
          <div className="max-w-measure text-center font-sans text-md text-n-60">
            <p className="flex items-center justify-center gap-2">
              <Sparkles className="size-4 text-rust" /> What should we get done?
            </p>
            <p className="mt-1 text-xs text-n-50">
              Mention a node or file with @, or run a Skill with the spark.
            </p>
          </div>

          {/* Suggested-action pills — hidden during onboarding so the
              pinned overlay prompt is the only call-to-action. */}
          {!suppressComposerSuggestions && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-6 max-w-sm"
            >
              <SuggestionPills
                className="justify-center"
                suggestions={SUGGESTED_ACTIONS.map((action) => ({
                  label: action.label,
                  icon: action.icon,
                }))}
                onSelect={(suggestion) => {
                  const action = SUGGESTED_ACTIONS.find(
                    (a) => a.label === suggestion.label,
                  );
                  if (action) handleSuggestedAction(action);
                }}
              />
            </motion.div>
          )}
        </div>
      )}

      <MessageInput
        flowSessionId={flowSessionId}
        canvasId={canvasId}
        onSend={onSendMessage}
        // Hydrate the scripted prompt only into the onboarding session's
        // composer — never a regular canvas-agent chat the user opened.
        defaultValue={
          isOnboardingSession ? pinnedComposerPrompt?.message : undefined
        }
        referencedEntities={referencedEntities}
        attachedFiles={uploadedFiles}
        onAttachFile={onAttachFile}
        onRemoveFile={onRemoveFile}
        sendButtonDisabled={isSendDisabled || isLoading}
        editingLocked={isOnboardingSession}
        controlSet={MessageInputControlSet.CANVAS_CHAT}
        location="sidebar"
        placeholder="What would you like me to do?"
        autoFocus={!isMessagesPending && !hasMessages}
        className="shrink-0 px-3 pb-3"
        classNames={CANVAS_SESSION_MESSAGE_INPUT_CLASSNAMES}
        agentReasoningEffort={reasoningEffort}
        onAgentReasoningEffortChange={setReasoningEffort}
        middleToolbarSlot={
          referencedEntities.length > 0 ? (
            <ReferencedEntitiesPills
              entities={referencedEntities}
              onRemove={onRemoveEntity}
              className="px-0 py-0"
            />
          ) : null
        }
      />
    </div>
  );
}

/** Floating canvas agent chat — header, session chrome, and chat body */
export function CanvasSessionPaneContent({
  canvasId,
  flowSessionId,
  canGoBack,
  onGoBack,
  onClose,
  isChatExpanded,
  onToggleExpand,
  onOpenSession,
  onMenuOpenChange,
  referencedEntities,
  onRemoveEntity,
  clearTextSelectionsFromMessage,
}: CanvasSessionPaneContentProps) {
  const queryClient = useQueryClient();
  const createFlowSession = useCreateFlowSession();
  const deleteFlowSession = useDeleteFlowSession();
  const [showSessionSelector, setShowSessionSelector] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Get the current session
  const { data: currentSession } = useFlowSessionById(flowSessionId || "", {
    enabled: !!flowSessionId,
  });

  // Get all sessions for this canvas; only main agent sessions appear in the
  // dropdown — sub-agent sessions (action_item_id set) are shown in the Explorer.
  const { data: existingSessions = [] } = useCanvasFlowSessions(canvasId, {
    enabled: !!canvasId,
  });
  const mainSessions = existingSessions.filter((s) => !s.action_item_id);

  // Define callbacks before early return
  const handleCreateNew = useCallback(async () => {
    if (!canvasId) return;

    setIsCreatingNew(true);
    try {
      const newSession = await createFlowSession.mutateAsync({
        mode: FLOW_SESSION_MODES.CANVAS_CHAT,
        class: "canvas",
        canvas_id: canvasId,
      });

      // Invalidate sessions list so dropdown appears when we now have multiple sessions
      queryClient.invalidateQueries({
        queryKey: ["canvas-flow-sessions", canvasId],
      });

      onOpenSession(newSession.id, "replace");

      setShowSessionSelector(false);
    } catch (error) {
      console.error("Failed to create new session:", error);
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error("Failed to create new session"),
        {
          tags: { component: "CanvasSessionPane", operation: "createSession" },
          extra: { canvasId },
        },
      );
    } finally {
      setIsCreatingNew(false);
    }
  }, [canvasId, createFlowSession, onOpenSession]);

  const handleSwitchSession = useCallback(
    (sessionId: string) => {
      if (!canvasId) return;

      onOpenSession(sessionId, "replace");
      setShowSessionSelector(false);
    },
    [canvasId, onOpenSession],
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!canvasId) return;

      try {
        await deleteFlowSession.mutateAsync(sessionId);

        // Invalidate the sessions list
        queryClient.invalidateQueries({
          queryKey: ["canvas-flow-sessions", canvasId],
        });

        // If we deleted the current session, navigate to another or close pane
        if (sessionId === flowSessionId) {
          const remainingSessions = mainSessions.filter(
            (s) => s.id !== sessionId,
          );

          if (remainingSessions.length > 0) {
            onOpenSession(remainingSessions[0].id, "replace");
          } else {
            onClose();
          }
        }

        // Keep dropdown open after delete (like CanvasTopBar does)
      } catch (error) {
        console.error("Failed to delete session:", error);
        Sentry.captureException(
          error instanceof Error
            ? error
            : new Error("Failed to delete session"),
          {
            tags: {
              component: "CanvasSessionPane",
              operation: "deleteSession",
            },
            extra: { canvasId, sessionId },
          },
        );
      }
    },
    [
      canvasId,
      flowSessionId,
      mainSessions,
      deleteFlowSession,
      queryClient,
      onOpenSession,
      onClose,
    ],
  );

  // Window header — name + session switcher on the left; the right cluster holds New chat, the
  // windowed/full-height size toggle, and minimize-to-FAB.
  const header = (
    <div className="shrink-0 border-b border-n-10 bg-surface-floating-chat">
      <div className="flex h-12 items-center justify-between gap-2.5 px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {canGoBack ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={onGoBack}
                    className="shrink-0"
                    aria-label="Go back"
                  >
                    <ArrowLeft />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Back</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {/* Session selector — a down-arrow dropdown to switch chats / start a new one,
              available whenever the canvas has at least one session. */}
          {mainSessions.length >= 1 ? (
            <DropdownMenu
              open={showSessionSelector}
              onOpenChange={(open) => {
                setShowSessionSelector(open);
                onMenuOpenChange?.(open);
              }}
            >
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    // Size to content (capped), not flex-1: the chevron sits
                    // right after the name/time and a gap remains before the
                    // right-hand button group. Long names truncate at the cap.
                    "-mx-1.5 flex min-w-0 max-w-[15rem] items-center gap-2 rounded-md px-1.5 py-1 text-left",
                    "outline-none transition-colors duration-150 ease-out hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust",
                  )}
                >
                  <span className="inline-flex size-[18px] shrink-0 items-center justify-center text-rust">
                    <MessagesSquare className="size-[18px]" />
                  </span>
                  <h2 className="truncate font-sans text-sm font-medium text-n-90">
                    {currentSession?.name || "Visual Agent"}
                  </h2>
                  {currentSession?.updated_at ? (
                    <span className="shrink-0 font-sans text-xs font-normal text-n-50">
                      {shortRelativeTime(currentSession.updated_at)}
                    </span>
                  ) : null}
                  <ChevronDown className="size-3.5 shrink-0 self-center text-n-40" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={8}
                className={cn(
                  "w-80 p-0",
                  "bg-surface-floating-chat/95 backdrop-blur-lg",
                  "rounded-2xl border",
                  "shadow-2xl shadow-black/20",
                )}
              >
                <div className="max-h-[360px] overflow-y-auto p-2">
                  {mainSessions.map((session) => (
                    <div key={session.id} className="group relative">
                      <DropdownMenuItem
                        onClick={() => handleSwitchSession(session.id)}
                        className={cn(
                          "flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 pr-10",
                          session.id === flowSessionId && "bg-accent",
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {session.name || "Untitled Session"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(session.updated_at), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                        {session.id === flowSessionId && (
                          <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                        )}
                      </DropdownMenuItem>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        className={cn(
                          "absolute right-2 top-1/2 -translate-y-1/2",
                          "rounded-md p-1.5 opacity-0 group-hover:opacity-100",
                          "transition-all hover:bg-destructive/10",
                        )}
                        aria-label="Delete session"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-t p-1.5">
                  <DropdownMenuItem
                    onClick={handleCreateNew}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    New chat
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="inline-flex size-[18px] shrink-0 items-center justify-center text-rust">
                <MessagesSquare className="size-[18px]" />
              </span>
              <h2 className="truncate font-sans text-sm font-medium text-n-90">
                Visual Agent
              </h2>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {/* New session */}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={handleCreateNew}
                  disabled={isCreatingNew || !canvasId}
                  aria-label="Start new chat"
                >
                  {isCreatingNew ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Plus />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Start new chat</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Size class: windowed ↔ full-height (docked). */}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onToggleExpand}
                  aria-label={
                    isChatExpanded ? "Restore window" : "Expand to full height"
                  }
                >
                  {isChatExpanded ? <Minimize2 /> : <Maximize2 />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isChatExpanded ? "Restore window" : "Expand to full height"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Minimize to the FAB launcher. */}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  aria-label="Minimize"
                >
                  <Minus />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Minimize</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );

  // Early return AFTER all hooks
  if (!canvasId) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">No canvas ID provided</p>
      </div>
    );
  }

  if (!flowSessionId) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">No session provided</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface-floating-chat">
      {header}

      {/* Wrap in all required providers */}
      <DropZoneProvider
        key={flowSessionId}
        className="flex-1 min-h-0 flex flex-col"
      >
        <MessageInputProvider>
          <SessionProvider initialSessionId={flowSessionId}>
            <CanvasSessionPaneInner
              flowSessionId={flowSessionId}
              canvasId={canvasId}
              referencedEntities={referencedEntities}
              onRemoveEntity={onRemoveEntity}
              clearTextSelectionsFromMessage={clearTextSelectionsFromMessage}
            />
          </SessionProvider>
        </MessageInputProvider>
      </DropZoneProvider>
    </div>
  );
}
